import React, { useState, useEffect, useCallback } from "react";
import {
    refreshTimesheetValidation, getTimesheetValidationQueue, generateAllClearedTimesheets,
    approveCheck1, rejectCheck1EntireTimesheet, actionCheck2Lines, getTimesheetValidationDetail,
    Check1QueueItem, Check2QueueItem, ReadyToGenerateItem, TimesheetValidationDetail,
} from "../services/api";

const secondaryBtnStyle: React.CSSProperties = {
    backgroundColor: "white", color: "#5C5D5E", border: "1px solid #ddd",
    borderRadius: "4px", padding: "8px 18px", cursor: "pointer", fontSize: "13px",
};
const primaryBtnStyle: React.CSSProperties = {
    backgroundColor: "#F18E00", color: "white", border: "none", borderRadius: "4px",
    padding: "6px 16px", cursor: "pointer", fontSize: "12px", fontWeight: "bold",
};
const dangerBtnStyle: React.CSSProperties = {
    backgroundColor: "white", color: "#C00000", border: "1px solid #C00000", borderRadius: "4px",
    padding: "6px 16px", cursor: "pointer", fontSize: "12px", fontWeight: "bold",
};
const sectionStyle: React.CSSProperties = {
    backgroundColor: "white", border: "1px solid #e0e0e0", borderRadius: "6px",
    padding: "16px 20px", marginBottom: "24px",
};
const bannerStyle: React.CSSProperties = {
    fontSize: "12px", color: "#666", marginBottom: "14px",
};
const emptyStyle: React.CSSProperties = {
    fontSize: "13px", color: "#999", padding: "10px 0",
};
const thStyle: React.CSSProperties = {
    textAlign: "left", padding: "8px 10px", fontSize: "12px",
    color: "white", backgroundColor: "#5C5D5E", whiteSpace: "nowrap",
};
const tdStyle: React.CSSProperties = {
    textAlign: "left", padding: "8px 10px", fontSize: "13px",
    borderBottom: "1px solid #f0f0f0", whiteSpace: "nowrap",
};
const textareaStyle: React.CSSProperties = {
    width: "100%", padding: "8px 10px", fontSize: "13px",
    border: "1px solid #ddd", borderRadius: "4px", boxSizing: "border-box",
    fontFamily: "inherit", resize: "vertical",
};

const money = (v: number | null | undefined): string => {
    if (v === null || v === undefined) return "—";
    const neg = v < 0;
    const s = Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return neg ? `($${s})` : `$${s}`;
};

const Spinner: React.FC = () => (
    <>
        <style>{"@keyframes tv-spin { to { transform: rotate(360deg); } }"}</style>
        <div style={{
            width: "14px", height: "14px", borderRadius: "50%",
            border: "2px solid #ffffff88", borderTopColor: "#fff",
            animation: "tv-spin 0.8s linear infinite", display: "inline-block",
        }} />
    </>
);

const TimesheetValidation: React.FC = () => {
    const [check1Queue, setCheck1Queue] = useState<Check1QueueItem[]>([]);
    const [check2Queue, setCheck2Queue] = useState<Check2QueueItem[]>([]);
    const [readyToGenerate, setReadyToGenerate] = useState<ReadyToGenerateItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [lastRefreshSummary, setLastRefreshSummary] = useState<{ cleared: number; still_pending: number } | null>(null);
    const [approvingId, setApprovingId] = useState<number | null>(null);

    // Check #1 reject (whole timesheet, single comment)
    const [rejectingTimesheetId, setRejectingTimesheetId] = useState<number | null>(null);
    const [rejectReason, setRejectReason] = useState("");
    const [rejectSubmitting, setRejectSubmitting] = useState(false);
    const [rejectError, setRejectError] = useState<string | null>(null);

    // Check #2 per-line Approve/Reject (2026-08-04, v30): approve is a
    // one-click admin override, reject reveals a reason box first.
    // check2ActingLineId disables buttons on whichever line is mid-request.
    const [check2ActingLineId, setCheck2ActingLineId] = useState<number | null>(null);
    const [check2RejectingLineId, setCheck2RejectingLineId] = useState<number | null>(null);
    const [check2RejectReason, setCheck2RejectReason] = useState("");
    const [check2LineError, setCheck2LineError] = useState<Record<number, string>>({});

    // Generate
    const [generating, setGenerating] = useState(false);
    const [generateSummary, setGenerateSummary] = useState<string | null>(null);

    // Ready to Generate: one "skip this timesheet" checkbox per row
    // (BACKLOG.md #17, 2026-09-10; corrected 2026-09-10 -- the first
    // version of this worked at the individual-line level, which was not
    // what Pete asked for) -- default checked/included, so this set only
    // ever holds whole timesheet ids an admin has explicitly unchecked.
    // Unchecking a row means "don't generate this one right now" -- it
    // stays at validation_status "cleared" and simply reappears here next
    // time, unchanged.
    const [excludedTimesheetIds, setExcludedTimesheetIds] = useState<Set<number>>(new Set());

    // Check #1 row detail expansion -- lets the admin see the full
    // cross-project breakdown of a flagged timesheet before deciding.
    const [expandedTimesheetId, setExpandedTimesheetId] = useState<number | null>(null);
    const [detailCache, setDetailCache] = useState<Record<number, TimesheetValidationDetail>>({});
    const [detailLoading, setDetailLoading] = useState<number | null>(null);
    const [detailError, setDetailError] = useState<string | null>(null);

    const load = useCallback(async (withRefresh: boolean) => {
        setError(null);
        if (withRefresh) setRefreshing(true); else setLoading(true);
        try {
            if (withRefresh) {
                const result = await refreshTimesheetValidation();
                setLastRefreshSummary({ cleared: result.cleared, still_pending: result.still_pending });
            }
            const data = await getTimesheetValidationQueue();
            setCheck1Queue(data.check1_queue);
            setCheck2Queue(data.check2_queue);
            setReadyToGenerate(data.ready_to_generate);
        } catch (e) {
            console.error("Failed to load Timesheet Validation queue", e);
            setError("Failed to load Timesheet Validation. Please try again.");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    // 2026-08-05 (Pete's explicit request, reverting part of the 2026-08-04
    // change): auto-refresh on every entry from nav, unlike Project Info.
    // Check #2 depends on live ProjectFinancialSnapshot data (pending/
    // remaining) that can go stale between the nightly job and whenever an
    // admin actually opens this screen -- e.g. a timesheet approved minutes
    // ago won't be reflected in Check #2 until something calls
    // refresh_and_evaluate(). Rather than rely on the admin remembering to
    // click Refresh first, call it automatically on mount; the manual
    // Refresh button still covers refreshing again without leaving the page.
    useEffect(() => {
        load(true);
    }, [load]);

    // 2026-08-04 (v31): quiet re-fetch after a row-level action (approve/
    // reject on either check) -- unlike load(), this never flips `loading`,
    // so the page doesn't unmount to the bare "Loading..." div and the
    // admin's scroll position/expanded rows are undisturbed. Only the
    // initial mount and the explicit Refresh button use the full load().
    const refetchQueue = useCallback(async () => {
        try {
            const data = await getTimesheetValidationQueue();
            setCheck1Queue(data.check1_queue);
            setCheck2Queue(data.check2_queue);
            setReadyToGenerate(data.ready_to_generate);
        } catch (e) {
            console.error("Failed to refresh Timesheet Validation queue", e);
            setError("Failed to load Timesheet Validation. Please try again.");
        }
    }, []);

    const handleApprove = async (timesheetId: number) => {
        setApprovingId(timesheetId);
        try {
            await approveCheck1(timesheetId);
            await refetchQueue();
        } catch (e) {
            console.error("Failed to approve timesheet", e);
        } finally {
            setApprovingId(null);
        }
    };

    const toggleDetail = async (timesheetId: number) => {
        if (expandedTimesheetId === timesheetId) {
            setExpandedTimesheetId(null);
            return;
        }
        setExpandedTimesheetId(timesheetId);
        setDetailError(null);
        if (detailCache[timesheetId]) return;
        setDetailLoading(timesheetId);
        try {
            const detail = await getTimesheetValidationDetail(timesheetId);
            setDetailCache(prev => ({ ...prev, [timesheetId]: detail }));
        } catch (e) {
            console.error("Failed to load timesheet detail", e);
            setDetailError("Failed to load timesheet details. Please try again.");
        } finally {
            setDetailLoading(null);
        }
    };

    const openReject = (timesheetId: number) => {
        setRejectingTimesheetId(timesheetId);
        setRejectReason("");
        setRejectError(null);
    };

    const cancelReject = () => {
        setRejectingTimesheetId(null);
        setRejectReason("");
        setRejectError(null);
    };

    const submitReject = async (timesheetId: number) => {
        const reason = rejectReason.trim();
        if (reason.length < 2) {
            setRejectError("A comment of at least 2 characters is required.");
            return;
        }
        setRejectSubmitting(true);
        setRejectError(null);
        try {
            await rejectCheck1EntireTimesheet(timesheetId, reason);
            setRejectingTimesheetId(null);
            setRejectReason("");
            await refetchQueue();
        } catch (e: any) {
            console.error("Failed to reject timesheet", e);
            setRejectError(e?.response?.data?.detail || "Failed to reject timesheet. Please try again.");
        } finally {
            setRejectSubmitting(false);
        }
    };

    const openCheck2Reject = (lineId: number) => {
        setCheck2RejectingLineId(lineId);
        setCheck2RejectReason("");
        setCheck2LineError(prev => ({ ...prev, [lineId]: "" }));
    };

    const cancelCheck2Reject = () => {
        setCheck2RejectingLineId(null);
        setCheck2RejectReason("");
    };

    const handleApproveCheck2Line = async (projectId: number, timesheetId: number, lineId: number) => {
        setCheck2ActingLineId(lineId);
        setCheck2LineError(prev => ({ ...prev, [lineId]: "" }));
        try {
            await actionCheck2Lines(projectId, timesheetId, [lineId], []);
            await refetchQueue();
        } catch (e: any) {
            console.error("Failed to approve Check #2 line", e);
            setCheck2LineError(prev => ({
                ...prev,
                [lineId]: e?.response?.data?.detail || "Failed to approve. Please try again.",
            }));
        } finally {
            setCheck2ActingLineId(null);
        }
    };

    const handleRejectCheck2Line = async (projectId: number, timesheetId: number, lineId: number) => {
        const reason = check2RejectReason.trim();
        if (reason.length < 2) {
            setCheck2LineError(prev => ({ ...prev, [lineId]: "A reason of at least 2 characters is required." }));
            return;
        }
        setCheck2ActingLineId(lineId);
        setCheck2LineError(prev => ({ ...prev, [lineId]: "" }));
        try {
            await actionCheck2Lines(projectId, timesheetId, [], [{ line_id: lineId, reason }]);
            setCheck2RejectingLineId(null);
            setCheck2RejectReason("");
            await refetchQueue();
        } catch (e: any) {
            console.error("Failed to reject Check #2 line", e);
            setCheck2LineError(prev => ({
                ...prev,
                [lineId]: e?.response?.data?.detail || "Failed to reject. Please try again.",
            }));
        } finally {
            setCheck2ActingLineId(null);
        }
    };

    const toggleExcludedTimesheet = (timesheetId: number) => {
        setExcludedTimesheetIds(prev => {
            const next = new Set(prev);
            if (next.has(timesheetId)) next.delete(timesheetId); else next.add(timesheetId);
            return next;
        });
    };

    const handleGenerateAll = async () => {
        setGenerating(true);
        setGenerateSummary(null);
        try {
            const result = await generateAllClearedTimesheets(Array.from(excludedTimesheetIds));
            const skippedSuffix = result.skipped ? `, skipped ${result.skipped}` : "";
            setGenerateSummary(`Generated ${result.generated} xls file${result.generated !== 1 ? "s" : ""}${skippedSuffix}.`);
            setExcludedTimesheetIds(new Set());
            await refetchQueue();
        } catch (e) {
            console.error("Failed to generate xls files", e);
            setGenerateSummary("Failed to generate. Please try again.");
        } finally {
            setGenerating(false);
        }
    };

    if (loading) return <div>Loading Timesheet Validation...</div>;

    if (error) {
        return (
            <div>
                <div style={{ color: "#C00000", fontSize: "14px", marginBottom: "12px" }}>{error}</div>
                <button onClick={() => load(true)} style={secondaryBtnStyle}>Retry</button>
            </div>
        );
    }

    return (
        <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "6px" }}>
                <h1 style={{ color: "#5C5D5E", margin: 0, fontSize: "22px" }}>Timesheet Validation</h1>
                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                    {refreshing && <span style={{ fontSize: "12px", color: "#666", display: "flex", alignItems: "center", gap: "6px" }}><Spinner /> Refreshing…</span>}
                    <button onClick={() => load(true)} style={secondaryBtnStyle} disabled={refreshing}>Refresh</button>
                </div>
            </div>
            <p style={{ color: "#666", marginTop: 0, marginBottom: "10px", fontSize: "13px" }}>
                Approved timesheets wait here until both checks below clear. Clearing both checks does NOT generate
                the xls automatically -- that only happens when you click Generate below. Data refreshes
                automatically overnight; click Refresh above for the latest.
            </p>
            {lastRefreshSummary && lastRefreshSummary.cleared > 0 && (
                <p style={{ color: "#2E7D32", fontSize: "12px", marginBottom: "20px" }}>
                    {lastRefreshSummary.cleared} timesheet{lastRefreshSummary.cleared !== 1 ? "s" : ""} cleared validation on
                    this refresh and {lastRefreshSummary.cleared !== 1 ? "are" : "is"} ready to generate below.
                </p>
            )}

            <div style={sectionStyle}>
                <h2 style={{ color: "#5C5D5E", fontSize: "15px", margin: "0 0 4px 0" }}>
                    User Timesheet Validation
                </h2>
                <div style={bannerStyle}>
                    Timesheets under 40 or over 50 total hours for the period. Click a row to see the full breakdown.
                    Approve to clear a legitimate case, or reject the entire timesheet back to the consultant with a
                    comment.
                </div>
                {check1Queue.length === 0 ? (
                    <div style={emptyStyle}>No issues — every pending timesheet is within the expected hour range.</div>
                ) : (
                    <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr>
                                    <th style={thStyle}>Employee</th>
                                    <th style={thStyle}>Period</th>
                                    <th style={thStyle}>Total Hours</th>
                                    <th style={thStyle}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {check1Queue.map(item => (
                                    <React.Fragment key={item.timesheet_id}>
                                        <tr
                                            onClick={() => toggleDetail(item.timesheet_id)}
                                            style={{ cursor: "pointer" }}
                                            onMouseEnter={e => { e.currentTarget.style.backgroundColor = "#FFF8EC"; }}
                                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = "transparent"; }}
                                        >
                                            <td style={tdStyle}>{item.employee_name}</td>
                                            <td style={tdStyle}>{item.period_start} – {item.period_end}</td>
                                            <td style={tdStyle}>{item.total_hours != null ? item.total_hours.toFixed(2) : "—"}</td>
                                            <td style={tdStyle} onClick={e => e.stopPropagation()}>
                                                <div style={{ display: "flex", gap: "8px" }}>
                                                    <button
                                                        style={{ ...primaryBtnStyle, opacity: approvingId === item.timesheet_id ? 0.6 : 1 }}
                                                        disabled={approvingId === item.timesheet_id || rejectingTimesheetId === item.timesheet_id}
                                                        onClick={() => handleApprove(item.timesheet_id)}
                                                    >
                                                        {approvingId === item.timesheet_id ? "Approving…" : "Approve"}
                                                    </button>
                                                    <button
                                                        style={dangerBtnStyle}
                                                        disabled={approvingId === item.timesheet_id}
                                                        onClick={() => rejectingTimesheetId === item.timesheet_id ? cancelReject() : openReject(item.timesheet_id)}
                                                    >
                                                        {rejectingTimesheetId === item.timesheet_id ? "Cancel" : "Reject"}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                        {expandedTimesheetId === item.timesheet_id && (
                                            <tr>
                                                <td colSpan={4} style={{ ...tdStyle, backgroundColor: "#FAFAFA" }}>
                                                    {detailLoading === item.timesheet_id ? (
                                                        <div style={{ fontSize: "12px", color: "#666", display: "flex", alignItems: "center", gap: "6px" }}>
                                                            <Spinner /> Loading details…
                                                        </div>
                                                    ) : detailError ? (
                                                        <div style={{ color: "#C00000", fontSize: "12px" }}>{detailError}</div>
                                                    ) : detailCache[item.timesheet_id] ? (
                                                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                                            <thead>
                                                                <tr>
                                                                    <th style={{ ...thStyle, backgroundColor: "#8a8b8c" }}>Project</th>
                                                                    <th style={{ ...thStyle, backgroundColor: "#8a8b8c" }}>Task</th>
                                                                    <th style={{ ...thStyle, backgroundColor: "#8a8b8c" }}>Date</th>
                                                                    <th style={{ ...thStyle, backgroundColor: "#8a8b8c" }}>Hours</th>
                                                                    <th style={{ ...thStyle, backgroundColor: "#8a8b8c" }}>Status</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {detailCache[item.timesheet_id].lines.map(line => (
                                                                    <tr key={line.line_id}>
                                                                        <td style={{ ...tdStyle, backgroundColor: "white" }}>{line.project_name}</td>
                                                                        <td style={{ ...tdStyle, backgroundColor: "white" }}>{line.task_name}</td>
                                                                        <td style={{ ...tdStyle, backgroundColor: "white" }}>{line.entry_date}</td>
                                                                        <td style={{ ...tdStyle, backgroundColor: "white" }}>{line.hours.toFixed(2)}</td>
                                                                        <td style={{ ...tdStyle, backgroundColor: "white" }}>{line.line_status}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    ) : null}
                                                </td>
                                            </tr>
                                        )}
                                        {rejectingTimesheetId === item.timesheet_id && (
                                            <tr>
                                                <td colSpan={4} style={{ ...tdStyle, backgroundColor: "#FDECEA" }}>
                                                    <div style={{ fontSize: "12px", color: "#5C5D5E", marginBottom: "6px" }}>
                                                        This sends the entire timesheet back to {item.employee_name} for revision.
                                                        Every line will need PM re-approval once resubmitted.
                                                    </div>
                                                    <textarea
                                                        value={rejectReason}
                                                        onChange={e => setRejectReason(e.target.value)}
                                                        placeholder="Reason for returning this timesheet (required, min 2 characters)"
                                                        rows={2}
                                                        style={textareaStyle}
                                                    />
                                                    {rejectError && (
                                                        <div style={{ color: "#C00000", fontSize: "12px", marginTop: "4px" }}>{rejectError}</div>
                                                    )}
                                                    <div style={{ marginTop: "8px" }}>
                                                        <button
                                                            style={{ ...dangerBtnStyle, opacity: rejectSubmitting ? 0.6 : 1 }}
                                                            disabled={rejectSubmitting}
                                                            onClick={() => submitReject(item.timesheet_id)}
                                                        >
                                                            {rejectSubmitting ? "Sending…" : "Confirm Reject & Return"}
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div style={sectionStyle}>
                <h2 style={{ color: "#5C5D5E", fontSize: "15px", margin: "0 0 4px 0" }}>
                    Project Budget Validation
                </h2>
                <div style={bannerStyle}>
                    Projects where approved-but-unvalidated hours (Pending) would push the project over budget.
                    Every pending line on that project needs Admin review here. Approve to let a line through
                    despite the overage (an explicit override, recorded per line), or Reject to send it back to
                    the consultant for correction.
                </div>
                {check2Queue.length === 0 ? (
                    <div style={emptyStyle}>No issues — no project is projected to go over budget.</div>
                ) : (
                    check2Queue.map(proj => (
                        <div key={proj.project_id} style={{ marginBottom: "18px", paddingBottom: "14px", borderBottom: "1px solid #f0f0f0" }}>
                            <div style={{ fontWeight: "bold", fontSize: "14px", color: "#5C5D5E", marginBottom: "4px" }}>
                                {proj.project_name}
                            </div>
                            <div style={{ fontSize: "12px", color: "#666", marginBottom: "8px" }}>
                                Budget {money(proj.budget)} · Actuals {money(proj.actuals)} · Pending {money(proj.pending)} ·{" "}
                                <span style={{ color: "#C00000", fontWeight: "bold" }}>Remaining {money(proj.remaining)}</span>
                            </div>
                            {proj.affected_timesheets.length === 0 ? (
                                <div style={emptyStyle}>No pending timesheets currently touch this project.</div>
                            ) : (
                                proj.affected_timesheets.map(ts => (
                                    <div key={ts.timesheet_id} style={{ marginBottom: "14px", border: "1px solid #e0e0e0", borderRadius: "6px", padding: "10px 12px" }}>
                                        <div style={{ fontSize: "13px", fontWeight: "bold", color: "#5C5D5E", marginBottom: "6px" }}>
                                            {ts.employee_name} — {ts.period_start} – {ts.period_end}
                                        </div>
                                        {ts.lines.map(line => (
                                            <div key={line.line_id} style={{ marginBottom: "8px" }}>
                                                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "8px" }}>
                                                    <span style={{ fontSize: "13px", color: "#333" }}>
                                                        {line.task_name} — {line.entry_date} — {line.hours.toFixed(2)} hrs
                                                        {line.amount != null && ` (${money(line.amount)})`}
                                                        {line.comments && <span style={{ color: "#999" }}> ({line.comments})</span>}
                                                    </span>
                                                    <div style={{ display: "flex", gap: "6px", flexShrink: 0 }}>
                                                        <button
                                                            style={{ ...primaryBtnStyle, opacity: check2ActingLineId === line.line_id ? 0.6 : 1 }}
                                                            disabled={check2ActingLineId === line.line_id}
                                                            onClick={() => handleApproveCheck2Line(proj.project_id, ts.timesheet_id, line.line_id)}
                                                        >
                                                            {check2ActingLineId === line.line_id ? "Approving…" : "Approve"}
                                                        </button>
                                                        <button
                                                            style={dangerBtnStyle}
                                                            disabled={check2ActingLineId === line.line_id}
                                                            onClick={() => check2RejectingLineId === line.line_id ? cancelCheck2Reject() : openCheck2Reject(line.line_id)}
                                                        >
                                                            {check2RejectingLineId === line.line_id ? "Cancel" : "Reject"}
                                                        </button>
                                                    </div>
                                                </div>
                                                {check2LineError[line.line_id] && (
                                                    <div style={{ color: "#C00000", fontSize: "12px", marginTop: "2px" }}>{check2LineError[line.line_id]}</div>
                                                )}
                                                {check2RejectingLineId === line.line_id && (
                                                    <div style={{ marginTop: "4px" }}>
                                                        <textarea
                                                            value={check2RejectReason}
                                                            onChange={e => setCheck2RejectReason(e.target.value)}
                                                            placeholder="Rejection reason (required, min 2 characters)"
                                                            rows={2}
                                                            style={textareaStyle}
                                                        />
                                                        <div style={{ marginTop: "6px" }}>
                                                            <button
                                                                style={{ ...dangerBtnStyle, opacity: check2ActingLineId === line.line_id ? 0.6 : 1 }}
                                                                disabled={check2ActingLineId === line.line_id}
                                                                onClick={() => handleRejectCheck2Line(proj.project_id, ts.timesheet_id, line.line_id)}
                                                            >
                                                                {check2ActingLineId === line.line_id ? "Rejecting…" : "Confirm Reject"}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ))
                            )}
                        </div>
                    ))
                )}
            </div>

            <div style={sectionStyle}>
                <h2 style={{ color: "#5C5D5E", fontSize: "15px", margin: "0 0 4px 0" }}>
                    Ready to Generate
                </h2>
                <div style={bannerStyle}>
                    Timesheets that passed both checks. Nothing happens until you click Generate -- xls files are
                    never created automatically.
                </div>
                {readyToGenerate.length === 0 ? (
                    <div style={emptyStyle}>Nothing is waiting to be generated right now.</div>
                ) : (
                    <>
                        {excludedTimesheetIds.size > 0 && (
                            <div style={{ fontSize: "12px", color: "#C00000", marginBottom: "10px" }}>
                                {excludedTimesheetIds.size} timesheet{excludedTimesheetIds.size !== 1 ? "s" : ""} unchecked --
                                will be skipped entirely when you click Generate (no xls, stays in this list for next time).
                            </div>
                        )}
                        <div style={{ overflowX: "auto", marginBottom: "12px" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                <thead>
                                    <tr>
                                        <th style={thStyle}>Generate</th>
                                        <th style={thStyle}>Employee</th>
                                        <th style={thStyle}>Period</th>
                                        <th style={thStyle}>Total Hours</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {readyToGenerate.map(item => (
                                        <tr key={item.timesheet_id}>
                                            <td style={tdStyle}>
                                                <input
                                                    type="checkbox"
                                                    checked={!excludedTimesheetIds.has(item.timesheet_id)}
                                                    onChange={() => toggleExcludedTimesheet(item.timesheet_id)}
                                                />
                                            </td>
                                            <td style={tdStyle}>{item.employee_name}</td>
                                            <td style={tdStyle}>{item.period_start} – {item.period_end}</td>
                                            <td style={tdStyle}>{item.total_hours != null ? item.total_hours.toFixed(2) : "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <button
                                style={{ ...primaryBtnStyle, padding: "10px 24px", fontSize: "14px", opacity: generating ? 0.6 : 1 }}
                                disabled={generating}
                                onClick={handleGenerateAll}
                            >
                                {generating ? "Generating…" : `Generate (${readyToGenerate.length - excludedTimesheetIds.size})`}
                            </button>
                            {generateSummary && (
                                <span style={{ fontSize: "12px", color: generateSummary.startsWith("Failed") ? "#C00000" : "#2E7D32" }}>
                                    {generateSummary}
                                </span>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default TimesheetValidation;
